import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import ClaimStatusTimeline from "./ClaimStatusTimeline";
import {
  CheckCircle2,
  Clock,
  XCircle,
  FileText,
  Send,
  DollarSign,
  Calendar,
  User,
  Stethoscope,
  History,
} from "lucide-react";

interface ClaimData {
  id: string;
  member_id: string;
  member_name: string;
  scheduled_date: string;
  visit_type: string;
  cpt_code: string | null;
  billing_status: string | null;
  billing_units: number | null;
  actual_duration_minutes: number | null;
  insurance_type: string;
  amount: number;
}

interface ClaimDetailDialogProps {
  claim: ClaimData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const billingStatusOptions = [
  { value: "pending", label: "Pending Review", icon: Clock, color: "text-amber-600" },
  { value: "submitted", label: "Submitted to Payer", icon: Send, color: "text-blue-600" },
  { value: "approved", label: "Approved/Paid", icon: CheckCircle2, color: "text-emerald-600" },
  { value: "denied", label: "Denied", icon: XCircle, color: "text-red-600" },
];

export default function ClaimDetailDialog({
  claim,
  open,
  onOpenChange,
}: ClaimDetailDialogProps) {
  const [newStatus, setNewStatus] = useState<string>("");
  const [notes, setNotes] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Reset state when claim changes
  const currentStatus = claim?.billing_status || "pending";

  const updateStatusMutation = useMutation({
    mutationFn: async ({ claimId, status }: { claimId: string; status: string }) => {
      const { error } = await supabase
        .from("telehealth_sessions")
        .update({ 
          billing_status: status,
          notes: notes || undefined,
        })
        .eq("id", claimId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing-claims"] });
      toast({
        title: "Claim updated",
        description: `Status changed to ${billingStatusOptions.find(s => s.value === newStatus)?.label}`,
      });
      setNewStatus("");
      setNotes("");
      onOpenChange(false);
    },
    onError: (error) => {
      toast({
        title: "Error updating claim",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleStatusUpdate = () => {
    if (!claim || !newStatus) return;
    updateStatusMutation.mutate({ claimId: claim.id, status: newStatus });
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Paid
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      case "denied":
        return (
          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
            <XCircle className="h-3 w-3 mr-1" />
            Denied
          </Badge>
        );
      case "submitted":
        return (
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
            <FileText className="h-3 w-3 mr-1" />
            Submitted
          </Badge>
        );
      default:
        return <Badge variant="outline">{status || "Unbilled"}</Badge>;
    }
  };

  const getInsuranceLabel = (type: string) => {
    const labels: Record<string, string> = {
      medicare: "Medicare",
      medicaid: "Medicaid",
      dual_eligible: "Dual Eligible",
    };
    return labels[type] || type;
  };

  if (!claim) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Claim Details
          </DialogTitle>
          <DialogDescription>
            Review claim lifecycle and update status for billing processing
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="lifecycle" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Lifecycle
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-6 mt-4">
            {/* Claim Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <User className="h-3 w-3" /> Patient
                </Label>
                <p className="font-medium">{claim.member_name}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Service Date
                </Label>
                <p className="font-medium">
                  {format(new Date(claim.scheduled_date), "MMM d, yyyy")}
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Stethoscope className="h-3 w-3" /> Visit Type
                </Label>
                <p className="font-medium capitalize">
                  {claim.visit_type.replace("_", " ")}
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">CPT Code</Label>
                <p className="font-mono font-medium">{claim.cpt_code || "N/A"}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Payer</Label>
                <p className="font-medium">{getInsuranceLabel(claim.insurance_type)}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <DollarSign className="h-3 w-3" /> Amount
                </Label>
                <p className="font-bold text-lg">${claim.amount.toLocaleString()}</p>
              </div>
            </div>

            <Separator />

            {/* Current Status */}
            <div className="space-y-2">
              <Label className="text-sm">Current Status</Label>
              <div>{getStatusBadge(currentStatus)}</div>
            </div>

          {/* Status Update */}
          <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
            <Label className="text-sm font-medium">Update Claim Status</Label>
            
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select new status..." />
                </SelectTrigger>
                <SelectContent>
                  {billingStatusOptions.map((option) => (
                    <SelectItem 
                      key={option.value} 
                      value={option.value}
                      disabled={option.value === currentStatus}
                    >
                      <div className="flex items-center gap-2">
                        <option.icon className={`h-4 w-4 ${option.color}`} />
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
              <Textarea
                placeholder="Add notes about this status change..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={500}
              />
            </div>

            <Button
              onClick={handleStatusUpdate}
              disabled={!newStatus || updateStatusMutation.isPending}
              className="w-full"
            >
              {updateStatusMutation.isPending ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                  Updating...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Update Status
                </>
              )}
            </Button>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            {currentStatus === "pending" && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => {
                  setNewStatus("submitted");
                  updateStatusMutation.mutate({ claimId: claim.id, status: "submitted" });
                }}
                disabled={updateStatusMutation.isPending}
              >
                <Send className="h-4 w-4 mr-2" />
                Submit to Payer
              </Button>
            )}
            {currentStatus === "submitted" && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  onClick={() => {
                    setNewStatus("approved");
                    updateStatusMutation.mutate({ claimId: claim.id, status: "approved" });
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark as Paid
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => {
                    setNewStatus("denied");
                    updateStatusMutation.mutate({ claimId: claim.id, status: "denied" });
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Mark as Denied
                </Button>
              </>
            )}
            {currentStatus === "denied" && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => {
                  setNewStatus("submitted");
                  updateStatusMutation.mutate({ claimId: claim.id, status: "submitted" });
                }}
                disabled={updateStatusMutation.isPending}
              >
                <Send className="h-4 w-4 mr-2" />
                Resubmit Claim
              </Button>
            )}
          </div>
          </TabsContent>

          <TabsContent value="lifecycle" className="mt-4">
            <ClaimStatusTimeline 
              currentStatus={currentStatus} 
              isDenied={currentStatus === "denied"}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
